import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Briefcase, Users as UsersIcon, ChevronDown, ChevronUp, PartyPopper } from "lucide-react";
import { getMySubPromoters, getMyAssistants } from "../../services/teamService";
import Card from "../../components/common/Card";
import Badge from "../../components/common/Badge";
import Tabs from "../../components/common/Tabs";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import { extractListResult } from "../../utils/apiHelpers";
import { color, radius } from "../../styles/theme";

const STATUS_VARIANT = {
  Active: "success",
  Pending: "warning",
  Deactivated: "danger",
};

/**
 * Cross-event team roster — a promoter's sub-promoters/business assistants
 * deduped across every event they're linked to, backed by two new backend
 * endpoints (see teamService.js). Read-only: per-event management (remove,
 * status change, permissions) stays in each event's own Participants tab —
 * this page is about visibility across events, not action.
 */
const TeamPage = () => {
  const { t } = useTranslation(["team", "common"]);
  const [tab, setTab] = useState("subPromoters");
  const [subPromoters, setSubPromoters] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getMySubPromoters(), getMyAssistants()])
      .then(([spRes, baRes]) => {
        setSubPromoters(extractListResult(spRes.data));
        setAssistants(extractListResult(baRes.data));
      })
      .catch(() => toast.error(t("loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  const tabs = [
    { key: "subPromoters", label: t("tabs.subPromoters", { count: subPromoters.length }), icon: Briefcase },
    { key: "businessAssistants", label: t("tabs.businessAssistants", { count: assistants.length }), icon: UsersIcon },
  ];
  const members = tab === "subPromoters" ? subPromoters : assistants;

  return (
    <div>
      <h2 style={s.heading}>{t("heading")}</h2>
      <p style={s.subheading}>{t("subheading")}</p>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {loading ? (
        <Loader />
      ) : members.length === 0 ? (
        <EmptyState icon={UsersIcon} text={t("empty")} />
      ) : (
        <div style={s.list}>
          {members.map((m) => (
            <TeamMemberRow key={m.UserId} member={m} />
          ))}
        </div>
      )}
    </div>
  );
};

const TeamMemberRow = ({ member }) => {
  const { t } = useTranslation("team");
  const [expanded, setExpanded] = useState(false);

  return (
    <Card style={s.card}>
      <div style={s.row} onClick={() => setExpanded((v) => !v)}>
        {member.LogoUrl ? (
          <img src={member.LogoUrl} alt="" style={s.avatar} onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div style={s.avatarFallback}>{(member.DisplayName || "?").charAt(0).toUpperCase()}</div>
        )}
        <div style={s.info}>
          <span style={s.name}>{member.DisplayName}</span>
          <span style={s.eventCount}><PartyPopper size={12} strokeWidth={2} /> {t("eventCount", { count: member.EventCount })}</span>
        </div>
        <button type="button" className="btn-ghost" style={s.expandBtn} aria-label={t("viewEvents")}>
          {expanded ? <ChevronUp size={15} strokeWidth={2} /> : <ChevronDown size={15} strokeWidth={2} />}
        </button>
      </div>

      {expanded && (
        <div style={s.eventsList}>
          {(member.Events || []).map((ev, i) => (
            <div key={ev.EventId ?? i} style={s.eventRow}>
              <span style={s.eventName}>{ev.EventName}</span>
              {ev.Status && <Badge variant={STATUS_VARIANT[ev.Status] || "neutral"}>{ev.Status}</Badge>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const s = {
  heading: { margin: "0 0 4px", fontSize: 21, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.4 },
  subheading: { margin: "0 0 20px", fontSize: 13, color: color.textMuted },
  list: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 640 },
  card: { padding: 0, overflow: "hidden" },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer" },
  avatar: { width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
  avatarFallback: {
    width: 40, height: 40, borderRadius: "50%", background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, flexShrink: 0,
  },
  info: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 },
  name: { fontSize: 14, fontWeight: 700, color: color.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  eventCount: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: color.textMuted },
  expandBtn: { width: 30, height: 30, padding: 0, borderRadius: radius.sm, flexShrink: 0 },
  eventsList: { borderTop: `1px solid ${color.border}`, padding: "8px 16px 12px" },
  eventRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0" },
  eventName: { fontSize: 13, color: color.textPrimary, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
};

export default TeamPage;
